import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Page from "~Common/Page";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useFetchDivisionList } from "~pages/Divisions/DivisionListPage/hooks/useFetchDivisionList.ts";
import { useGetDivisionTableColumns } from "~pages/Divisions/DivisionListPage/hooks/useGetDivisionTableColumns.tsx";
import type { DivisionListItem } from "~pages/Divisions/DivisionListPage/types/divisionListItem.ts";
import { useDivisionService } from "~pages/Divisions/hooks/useDivisionService.ts";

const DEFAULT_SORTER = { columnKey: "nameLocal", order: "ascend" as const };

const DivisionListPage = () => {
  const paginationConfig = useHandlePagination(20, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId, conferenceId } = useParams();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [massEditMode, setMassEditMode] = useState(false);

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.divisions.listPage;
  const columns = useGetDivisionTableColumns(massEditMode);

  const { resetPagination } = paginationConfig;

  const handleSearch = useCallback(
    (value: string) => {
      resetPagination();
      setSearchText(value);
    },
    [resetPagination],
  );

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      nameLocalContains: searchText,
      include: "conferences",
    }),
    [paginationConfig.pageSize, paginationConfig.offset, searchText, sortBy],
  );

  const { data, refetch, isPending } = useFetchDivisionList({
    customParams: listParams,
    enabled: sortBy !== null,
  });
  const { deleteDivision, isDeletePending } = useDivisionService();

  const conferenceName = data?.conferenceName;
  const pageTitle = conferenceName
    ? formatMessage({ id: "pageTitles.organization.divisions" }, { conferenceName })
    : formatMessage({ id: "divisions" });

  const getNavigateUrl = (divisionId = "") =>
    `/${customerId}/admin/${sport}/${organizationId}/conferences/${conferenceId}/divisions/manage/${divisionId}`;

  return (
    <Page title={pageTitle} tabTitle={pageTitle}>
      <DataTable<DivisionListItem>
        handleRefresh={async () => {
          await refetch();
        }}
        columns={columns}
        rowActions={{
          base: {
            manage: {
              onClick: (row) => navigate(getNavigateUrl(row.divisionId)),
            },
            delete: {
              onClick: (row) => deleteDivision(row.divisionId),
              loading: isDeletePending,
            },
          },
        }}
        rowKey={"divisionId"}
        rowNameKey={"nameLocal"}
        dataSource={data?.data || []}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.divisions`}
        isDataLoading={isPending}
        handleAdd={() => navigate(getNavigateUrl())}
        addButtonIntlKey={"division"}
        searchConfig={{
          handleSearch,
          searchText,
        }}
        columnSortConfig={{
          sorterLocalStorageKey: `${sport}.tables.settings.sort.divisions`,
          defaultSorter: DEFAULT_SORTER,
          onSortChange: setSortBy,
        }}
        permissionNames={[PermissionNames.DIVISION]}
        onMassEditModeChangedHandler={setMassEditMode}
        massEditMode={massEditMode}
        dataTableUiConfiguration={dataTableUiConfiguration}
        paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
      />
    </Page>
  );
};

export default DivisionListPage;
